<template>
  <div>
    <h3 class="display-4" @click="enableDebug($event)">Manage Customers</h3>

    <o-list-toolbar
      :filters-list="filter_list"
      :search-fields-list="search_field_list"
      :selected-filter="selected_filter"
      :list-options="list_options"
      @reset_table="reset_table"
      @options_changed="update_table($event)"
      @filter_changed="update_filter($event)"
      :schema="schema"
      :query-select-options="options"
      code-table
      @create="saveItem"
      :new-to="{ name: 'customer.create', params: { id: 0 } }"
      new-button-text="New Customer"
    >
      <template slot="middle">
        <o-list-multi-actions
          :schema="schema"
          :selection="selected"
          @delete="doDeleteItems"
          @merge="doMergeItems"
          enable-delete
          enable-merge
        />
      </template>
    </o-list-toolbar>

    <b-table
      striped
      bordered
      :fields="fields"
      :items="items"
      head-variant="light"
      class="o-list"
      thead-class="o-gradient-header"
      @row-selected="onRowSelected"
      :selectable="selected.selectable"
      :key="selected.selectable"
      :busy="working"
      no-local-sorting
      @sort-changed="change_sort"
      :sort-by="list_options.sort"
      :sort-desc="list_options.order === 'desc'"
    >
      <template v-slot:cell(_selected)="{ rowSelected }">
        <template v-if="rowSelected">
          <span aria-hidden="true">&check;</span>
          <span class="sr-only">Selected</span>
        </template>
        <template v-else>
          <span aria-hidden="true">&nbsp;</span>
          <span class="sr-only">Not selected</span>
        </template>
      </template>
      <template
        slot="top-row"
        slot-scope="{ columns, fields }"
        v-if="filterRow"
      >
        <td :key="field.key" class="p-0" v-for="field in fields">
          <span v-if="field.key === 'active_end_date'">
            <o-flatpickr-wrapper
              @change="filter_changed"
              class="rounded-0 px-0"
              v-model="list_options.qbe.active_end_date"
            ></o-flatpickr-wrapper>
          </span>
          <span v-if="field.key === 'active_start_date'">
            <o-flatpickr-wrapper
              @change="filter_changed"
              class="rounded-0 px-0"
              v-model="list_options.qbe.active_start_date"
            ></o-flatpickr-wrapper>
          </span>
          <span v-if="field.key === 'auto_part_a_required'">
            <o-boolean-select
              @change="filter_changed"
              class="rounded-0 px-0"
              v-model="list_options.qbe.auto_part_a_required"
            ></o-boolean-select>
          </span>
          <span v-if="field.key === 'code'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.code"
            ></b-form-input>
          </span>
          <span v-if="field.key === 'created_date'">
            <o-flatpickr-wrapper
              @change="filter_changed"
              class="rounded-0 px-0"
              v-model="list_options.qbe.created_date"
            ></o-flatpickr-wrapper>
          </span>
          <span v-if="field.key === 'email'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.email"
            ></b-form-input>
          </span>
          <span v-if="field.key === 'modified_date'">
            <o-flatpickr-wrapper
              @change="filter_changed"
              class="rounded-0 px-0"
              v-model="list_options.qbe.modified_date"
            ></o-flatpickr-wrapper>
          </span>
          <span v-if="field.key === 'name'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.name"
            ></b-form-input>
          </span>
          <span v-if="field.key === 'original_data'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.original_data"
            ></b-form-input>
          </span>
          <span v-if="field.key === 'part_a_supplier'">
            <o-form-select
              v-model="list_options.qbe.part_a_supplier"
              :options="optionPartASupplierItems"
              text-field="name"
              value-field="id"
              sort-field="name"
              blank-option-text=""
              :loading="optionSupplierWorking"
              @change="filter_changed"
              name="part_a_supplier"
            >
              <template v-slot:first>
                <option :value="null"></option>
              </template>
            </o-form-select>
          </span>
          <span v-if="field.key === 'replaced_data'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.replaced_data"
            ></b-form-input>
          </span>
        </td>
      </template>
      <template slot="cell(active_end_date)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'customer.edit', params: { id: item.id } }"
          :when="first_column_key === 'active_end_date'"
        >
          <date-display
            :value="item.active_end_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template slot="cell(active_start_date)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'customer.edit', params: { id: item.id } }"
          :when="first_column_key === 'active_start_date'"
        >
          <date-display
            :value="item.active_start_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template slot="cell(auto_part_a_required)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'customer.edit', params: { id: item.id } }"
          :when="first_column_key === 'auto_part_a_required'"
        >
          <boolean-display :value="item.auto_part_a_required"></boolean-display>
        </o-link-cell>
      </template>
      <template slot="cell(code)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'customer.edit', params: { id: item.id } }"
          :when="first_column_key === 'code'"
        >
          <default-display :value="item.code"></default-display>
        </o-link-cell>
      </template>
      <template slot="cell(created_date)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'customer.edit', params: { id: item.id } }"
          :when="first_column_key === 'created_date'"
        >
          <date-display
            :value="item.created_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template slot="cell(email)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'customer.edit', params: { id: item.id } }"
          :when="first_column_key === 'email'"
        >
          <default-display :value="item.email"></default-display>
        </o-link-cell>
      </template>
      <template slot="cell(inspectors)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'customer.edit', params: { id: item.id } }"
          :when="first_column_key === 'inspectors'"
        >
          <ul class="tableUL">
            <li v-for="ins in item.inspectors_list" :key="ins.id">
              {{ ins.name }}
            </li>
          </ul>
        </o-link-cell>
      </template>
      <template slot="cell(modified_date)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'customer.edit', params: { id: item.id } }"
          :when="first_column_key === 'modified_date'"
        >
          <date-display
            :value="item.modified_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template slot="cell(name)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'customer.edit', params: { id: item.id } }"
          :when="first_column_key === 'name'"
        >
          <default-display :value="item.name"></default-display>
        </o-link-cell>
      </template>
      <template slot="cell(original_data)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'customer.edit', params: { id: item.id } }"
          :when="first_column_key === 'original_data'"
        >
          {{ item.original_data }}
        </o-link-cell>
      </template>
      <template slot="cell(part_a_supplier)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'customer.edit', params: { id: item.id } }"
          :when="first_column_key === 'part_a_supplier'"
        >
          <lookup-display
            :list="optionSupplierItemsById"
            :value="item.part_a_supplier"
            display-field="name"
            default-value="-"
          ></lookup-display>
        </o-link-cell>
      </template>
      <template slot="cell(replaced_data)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'customer.edit', params: { id: item.id } }"
          :when="first_column_key === 'replaced_data'"
        >
          {{ item.replaced_data }}
        </o-link-cell>
      </template>

      <template slot="empty">
        <p>
          <emoticon-sad-icon large></emoticon-sad-icon>
        </p>
        No Data Found. Please
        <router-link :to="{ name: 'customer.create' }"
          >create a new item</router-link
        >
      </template>
    </b-table>

    <o-list-paging
      :limit="list_options.limit"
      :page-options="rows_per_page_items"
      :offset="list_options.offset"
      :total-items="total_items"
      @change-limit="change_limit"
      @change-page="change_page"
    />
  </div>
</template>

<style></style>

<script>
import {
  CreateCodeTableFetchMixin,
  CreateGroupPermissionMixin,
  CreateListPageMixin,
  MultiActionsMixin,
} from "../../../vue-ocom/mixins";
import { default as SupplierTypeMixin } from "../../job/mixins/supplier_type_mixin";

import {
  fieldToTableColumn,
  GetSearchFields,
} from "../../../vue-ocom/schema/schema_utils";
import { cleanUpModel } from "../../../vue-ocom/schema/schema_vuetify";
import schema from "../../schema/index";

import OListToolbar from "../../../vue-ocom/components/list/OListToolbar";
import OListPaging from "../../../vue-ocom/components/list/OListPaging";
import DefaultDisplay from "../../../vue-ocom/components/display/DefaultDisplay";
import DateDisplay from "../../../vue-ocom/components/display/DateDisplay";
import NumberDisplay from "../../../vue-ocom/components/display/NumberDisplay";
import BooleanDisplay from "../../../vue-ocom/components/display/BooleanDisplay";
import LookupDisplay from "../../../vue-ocom/components/display/LookupDisplay";
import EmoticonSadIcon from "vue-material-design-icons/EmoticonSad";
import EmailIcon from "vue-material-design-icons/Email";
import LinkIcon from "vue-material-design-icons/Link";

import { filter, includes } from "lodash";

export default {
  components: {
    EmoticonSadIcon,
    EmailIcon,
    LinkIcon,
    OListPaging,
    OListToolbar,
    DateDisplay,
    DefaultDisplay,
    BooleanDisplay,
    NumberDisplay,
    LookupDisplay,
  },
  mixins: [
    CreateListPageMixin("Customer", "customer"),
    MultiActionsMixin,
    CreateGroupPermissionMixin("Customer"),
    CreateCodeTableFetchMixin(["Supplier", "CodeSupplierType"]),
    SupplierTypeMixin,
  ],
  data: function () {
    return {
      schemaModel: cleanUpModel(
        schema.Customer,
        {}
      ) /*Use this.schema instead */,

      search_field_list: GetSearchFields(schema.Customer),

      filterRow: true /* Set to False to disable filter row */,
      filter_list: [
        { name: "Active", code: "active", options: { offset: 0, sort: "id" } },
        {
          name: "Non Active",
          code: "inactive",
          options: { offset: 0, sort: "id" },
        },
        { name: "All", code: "all", options: { offset: 0, sort: "id" } },
      ],

      user_column_selection: null /* If user can customise the columns set them here */,
      column_override_params: {
        /* Put any column overrides here key by fieldname */
      },
    };
  },
  computed: {
    options() {
      return {
        Supplier: {
          options: this.optionSupplierItems,
          textField: "name",
          valueField: "id",
        },
      };
    },
    fields() {
      const vm = this;
      const selectedColumns = this.user_column_selection || [
        "name",
        "code",
        "auto_part_a_required",
        "part_a_supplier",
        "email",
        "inspectors",
        "active_start_date",
        "active_end_date",
      ];
      const fields = selectedColumns.map(function (item) {
        return fieldToTableColumn(
          vm.schema,
          item,
          vm.column_override_params[item]
        );
      });

      return this.addSelectionColums(
        this.filterTableColumns(this.schema, fields)
      );
    },
    first_column_key() {
      if (this.fields) {
        return this.fields[0].key;
      } else {
        return null;
      }
    },
    optionPartASupplierItems() {
      return filter(this.optionSupplierItems, function (item) {
        return includes(item.supplier_type_names, "Part A");
      });
    },
  },
};
</script>

<style>
ul.tableUL li {
  list-style-type: none;
}
ul.tableUL {
  list-style-type: unset;
  padding-left: 0;
}
</style>
